import { BaseError, NetworkError, TimeoutError, ApiError, AuthError, ErrorCode } from './errors'
import { logger } from './logger'
import { circuitBreakerAnalytics, CircuitBreakerEvent } from './circuitBreakerMetrics'

/**
 * Enhanced circuit breaker pattern implementation with metrics and monitoring
 */
export class CircuitBreaker {
  private failures = 0
  private lastFailureTime = 0
  private lastSuccessTime = 0
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED'
  private requestCount = 0
  private responseTimeSum = 0
  
  constructor(
    private readonly name: string,
    private readonly threshold: number = 5,
    timeout: number = 60000, // 1 minute - unused but kept for API compatibility
    private readonly resetTimeout: number = 30000, // 30 seconds
    private readonly healthCheckUrl?: string
  ) {
    // timeout parameter is kept for backward compatibility but not used
    void timeout
    
    // Initialize metrics for this circuit breaker
    circuitBreakerAnalytics.initializeMetrics(this.name)
  }

  async execute<T>(
    operation: () => Promise<T>,
    fallback?: () => T | Promise<T>
  ): Promise<T> {
    const startTime = Date.now()
    
    if (this.state === 'OPEN') {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime
      
      if (timeSinceLastFailure > this.resetTimeout) {
        logger.info(`Circuit breaker ${this.name}: attempting reset to HALF_OPEN`)
        this.state = 'HALF_OPEN'
        circuitBreakerAnalytics.recordEvent(this.name, CircuitBreakerEvent.HALF_OPENED)
      } else {
        logger.warn(`Circuit breaker ${this.name}: OPEN, using fallback`)
        circuitBreakerAnalytics.recordEvent(this.name, CircuitBreakerEvent.FALLBACK_USED, {
          timeSinceFailure: timeSinceLastFailure,
          resetTimeoutRemaining: this.resetTimeout - timeSinceLastFailure
        })
        
        if (fallback) {
          return fallback()
        }
        throw new NetworkError(`Circuit breaker is open for ${this.name}`, {
          context: { 
            circuitBreaker: this.name,
            state: this.state,
            failures: this.failures,
            timeSinceLastFailure 
          }
        })
      }
    }

    this.requestCount++

    try {
      const result = await operation()
      const responseTime = Date.now() - startTime
      this.onSuccess(responseTime)
      return result
    } catch (error) {
      this.onFailure(error)
      throw error
    }
  }

  private onSuccess(responseTime?: number) {
    this.lastSuccessTime = Date.now()

    if (responseTime) {
      this.responseTimeSum += responseTime
    }

    if (this.failures > 0) {
      logger.debug(
        `Circuit breaker ${this.name}: resetting failure count after successful request`,
        {
          previousFailures: this.failures,
          state: this.state,
        }
      )
    }

    this.failures = 0
    this.lastFailureTime = 0

    circuitBreakerAnalytics.recordEvent(this.name, CircuitBreakerEvent.REQUEST_SUCCESS, {
      responseTime,
      state: this.state
    })

    if (this.state === 'HALF_OPEN') {
      logger.info(`Circuit breaker ${this.name}: reset to CLOSED after successful test`)
      this.state = 'CLOSED'
      circuitBreakerAnalytics.recordEvent(this.name, CircuitBreakerEvent.CLOSED)
    }
  }

  private onFailure(error?: unknown) {
    this.failures++
    this.lastFailureTime = Date.now()

    const errorInfo = {
      errorType: error instanceof Error ? error.constructor.name : 'Unknown',
      errorMessage: error instanceof Error ? error.message : String(error),
      statusCode: error instanceof ApiError ? error.statusCode : undefined
    }

    circuitBreakerAnalytics.recordEvent(this.name, CircuitBreakerEvent.REQUEST_FAILURE, {
      failures: this.failures,
      threshold: this.threshold,
      state: this.state,
      ...errorInfo
    })

    if (this.failures >= this.threshold) {
      // ✅ OPTIMIZATION: Circuit breaker state changes are operational events, not errors
      // Keep as application logs only, don't send to Sentry
      logger.warn(`Circuit breaker ${this.name}: threshold reached (${this.failures}/${this.threshold}), opening circuit`)
      this.state = 'OPEN'
      circuitBreakerAnalytics.recordEvent(this.name, CircuitBreakerEvent.OPENED, {
        threshold: this.threshold,
        finalFailures: this.failures,
        ...errorInfo
      })
    }
  }

  reset() {
    const wasOpen = this.state === 'OPEN'
    
    this.failures = 0
    this.lastFailureTime = 0
    this.state = 'CLOSED'
    
    circuitBreakerAnalytics.recordEvent(this.name, CircuitBreakerEvent.MANUAL_RESET, {
      wasOpen,
      previousFailures: this.failures
    })
    
    logger.info(`Circuit breaker ${this.name}: manually reset to CLOSED`)
  }

  getState() {
    return {
      name: this.name,
      state: this.state,
      failures: this.failures,
      lastFailureTime: this.lastFailureTime,
      lastSuccessTime: this.lastSuccessTime,
      threshold: this.threshold,
      resetTimeout: this.resetTimeout,
      requestCount: this.requestCount,
      averageResponseTime: this.requestCount > 0 ? this.responseTimeSum / this.requestCount : 0
    }
  }

  // Health check functionality
  async performHealthCheck(): Promise<boolean> {
    if (!this.healthCheckUrl) {
      return true // No health check configured
    }

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      const response = await fetch(this.healthCheckUrl, {
        method: 'GET',
        signal: controller.signal
      })
      clearTimeout(timeoutId)
      return response.ok
    } catch {
      return false
    }
  }

  // Get metrics for this circuit breaker
  getMetrics() {
    return circuitBreakerAnalytics.getMetrics(this.name)
  }

  // Get detailed health status
  getHealthStatus() {
    const metrics = this.getMetrics()
    const state = this.getState()
    
    return {
      ...state,
      metrics,
      isHealthy: this.state === 'CLOSED' && (metrics?.failureRate || 0) < 0.1,
      uptime: metrics?.uptimePercentage || 100,
      recommendations: this.getRecommendations()
    }
  }

  private getRecommendations(): string[] {
    const recommendations: string[] = []
    const metrics = this.getMetrics()
    
    if (this.state === 'OPEN') {
      recommendations.push('Service is currently unavailable. Wait for automatic recovery or check service health.')
    }
    
    if (metrics && metrics.failureRate > 0.1) {
      recommendations.push('High failure rate detected. Consider investigating underlying service issues.')
    }
    
    if (metrics && metrics.averageResponseTime > 5000) {
      recommendations.push('Slow response times detected. Consider performance optimization.')
    }
    
    if (this.failures > this.threshold * 0.8) {
      recommendations.push('Approaching failure threshold. Monitor service closely.')
    }
    
    return recommendations
  }
}

/**
 * Retry mechanism with exponential backoff
 */
export interface RetryOptions {
  maxAttempts?: number
  initialDelay?: number
  maxDelay?: number
  backoffMultiplier?: number
  shouldRetry?: (error: unknown, attempt: number) => boolean
  onRetry?: (error: unknown, attempt: number, delay: number) => void
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    backoffMultiplier = 2,
    shouldRetry = defaultShouldRetry,
    onRetry
  } = options

  let lastError: unknown

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error

      if (attempt === maxAttempts || !shouldRetry(error, attempt)) {
        throw error
      }

      const delay = Math.min(
        initialDelay * Math.pow(backoffMultiplier, attempt - 1),
        maxDelay
      )

      if (onRetry) {
        onRetry(error, attempt, delay)
      }

      logger.warn(`Retry attempt ${attempt}/${maxAttempts} after ${delay}ms`, {
        error: error instanceof Error ? error.message : String(error),
        attempt,
        delay
      })

      await sleep(delay)
    }
  }

  throw lastError
}

function defaultShouldRetry(error: unknown, attempt: number): boolean {
  // Don't retry if we've exhausted attempts
  if (attempt >= 3) return false

  // Always retry network errors
  if (error instanceof NetworkError || error instanceof TimeoutError) {
    return true
  }

  // Retry specific API errors
  if (error instanceof ApiError) {
    // Retry server errors (5xx)
    if (error.statusCode && error.statusCode >= 500) {
      return true
    }
    
    // Retry rate limit errors
    if (error.statusCode === 429) {
      return true
    }

    // Don't retry client errors (4xx)
    if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
      return false
    }
  }

  // Don't retry auth errors
  if (error instanceof AuthError) {
    return false
  }

  // Retry other errors on first attempt only
  return attempt === 1
}

/**
 * Error recovery strategies
 */
export interface RecoveryStrategy {
  canRecover(error: BaseError): boolean
  recover(error: BaseError): Promise<void>
}

export class AuthRecoveryStrategy implements RecoveryStrategy {
  constructor(private refreshAuth: () => Promise<void>) {}

  canRecover(error: BaseError): boolean {
    return error.code === ErrorCode.UNAUTHORIZED_ERROR || 
           error.code === ErrorCode.SESSION_EXPIRED_ERROR
  }

  async recover(error: BaseError): Promise<void> {
    logger.info('Attempting auth recovery')
    try {
      await this.refreshAuth()
      logger.info('Auth recovery successful')
    } catch (refreshError) {
      logger.error('Auth recovery failed', refreshError)
      throw error
    }
  }
}

export class NetworkRecoveryStrategy implements RecoveryStrategy {
  private retryCount = 0
  private readonly maxRetries = 3

  canRecover(error: BaseError): boolean {
    return (error.code === ErrorCode.NETWORK_ERROR || 
            error.code === ErrorCode.TIMEOUT_ERROR) &&
            this.retryCount < this.maxRetries
  }

  async recover(error: BaseError): Promise<void> {
    this.retryCount++
    logger.info(`Network recovery attempt ${this.retryCount}/${this.maxRetries}`)
    
    // Wait for network to stabilize
    await sleep(2000 * this.retryCount)
    
    // Check if we're back online
    if (!navigator.onLine) {
      throw error
    }
    
    logger.info('Network appears to be restored')
  }

  reset() {
    this.retryCount = 0
  }
}

export class ErrorRecoveryManager {
  private strategies: RecoveryStrategy[] = []

  addStrategy(strategy: RecoveryStrategy) {
    this.strategies.push(strategy)
  }

  async attemptRecovery(error: BaseError): Promise<boolean> {
    for (const strategy of this.strategies) {
      if (strategy.canRecover(error)) {
        try {
          await strategy.recover(error)
          return true
        } catch (recoveryError) {
          logger.error('Recovery strategy failed', {
            strategy: strategy.constructor.name,
            error: recoveryError
          })
        }
      }
    }
    return false
  }
}

/**
 * Global error handler
 */
export class GlobalErrorHandler {
  private errorCounts = new Map<string, number>()
  private readonly maxErrorsPerType = 10
  private readonly errorWindow = 60000 // 1 minute

  constructor(
    private readonly onCriticalError?: (error: BaseError) => void
  ) {
    this.setupGlobalHandlers()
  }

  private setupGlobalHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      logger.error('Unhandled promise rejection', event.reason)
      this.handleError(event.reason)
      event.preventDefault()
    })

    // Handle global errors
    window.addEventListener('error', (event) => {
      logger.error('Global error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      })
      this.handleError(event.error || event.message)
      event.preventDefault()
    })
  }

  handleError(error: unknown) {
    const baseError = error instanceof BaseError 
      ? error 
      : ErrorFactory.fromError(error, 'Global Error Handler')

    // Track error frequency
    const errorKey = `${baseError.code}-${baseError.message}`
    const count = (this.errorCounts.get(errorKey) || 0) + 1
    this.errorCounts.set(errorKey, count)

    // Clean up old error counts
    setTimeout(() => {
      this.errorCounts.delete(errorKey)
    }, this.errorWindow)

    // Check if we're seeing too many errors
    if (count > this.maxErrorsPerType) {
      logger.error('Error threshold exceeded', {
        errorKey,
        count,
        threshold: this.maxErrorsPerType
      })

      if (this.onCriticalError) {
        this.onCriticalError(baseError)
      }
    }
  }

  getErrorStats() {
    return Array.from(this.errorCounts.entries()).map(([key, count]) => ({
      key,
      count
    }))
  }
}

/**
 * Utility functions
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function isRetryableError(error: unknown): boolean {
  if (error instanceof BaseError) {
    return error.isRecoverable() && defaultShouldRetry(error, 1)
  }
  return false
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof BaseError) {
    return error.getUserMessage()
  }
  if (error instanceof Error) {
    return error.message
  }
  return String(error)
}

// Import ErrorFactory to avoid circular dependency
import { ErrorFactory } from './errors'
